import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  PageHead,
  Pill,
  SelectX,
  Skeleton,
  StatCard,
  StatusPill,
  Textarea,
  Timeline,
  Toggle,
  useToast,
} from "@guestpilot/ui-kit";
import { Building20Regular, Flag20Regular, Money20Regular, People20Regular } from "@fluentui/react-icons";
import { fieldErrorsOf, useGet, useHandleApiError, useWrite, type Schema } from "../api";
import { ConfirmDialog, PLAN_OPTIONS } from "../components";
import { formatDate, formatDateTime, formatMoney, formatNumber } from "../format";
import { STATUS_LABEL } from "./Dashboard";

type Detail = Schema<"CustomerDetail">;
type Flag = Schema<"FeatureFlag">;
type Note = Schema<"SupportNote">;
type Request = Schema<"LifecycleRequest">;

type Action = "plan" | "cancel" | "suspend" | "lift" | "trial" | "internal" | "request" | null;

const CATEGORIES = [
  { value: "payment", label: "Payment" },
  { value: "abuse", label: "Abuse" },
  { value: "fraud", label: "Fraud" },
  { value: "legal", label: "Legal" },
  { value: "customer_request", label: "Customer request" },
  { value: "other", label: "Other" },
];
const REQUEST_TYPES = [
  { value: "export", label: "Export all data" },
  { value: "purge", label: "Purge the tenant" },
  { value: "switch", label: "Switch to another provider" },
  { value: "redact", label: "Redact text entered by mistake (Art. 9)" },
  { value: "anonymise_person", label: "Anonymise a person" },
];
const REQUEST_STATUS: Record<string, string> = {
  open: "Open",
  scheduled: "Scheduled",
  executed: "Executed",
  cancelled: "Cancelled",
};

/** The one place staff work an account (BR-251…BR-253, BR-257, BR-263, BR-264); account-level data only (BR-243). */
export function CustomerDetail() {
  const { organizationId = "" } = useParams();
  const path = `/customers/${organizationId}`;
  const q = useGet<Detail>(path);
  const [action, setAction] = useState<Action>(null);
  if (q.isError)
    return (
      <Banner tone="danger" action={<Button onClick={() => void q.refetch()}>Retry</Button>}>
        Could not load this customer.
      </Banner>
    );
  const c = q.data;
  if (!c) return <Skeleton rows={6} />;
  const sus = c.suspension ?? "none";
  const canExtendTrial = c.status === "trial" && !c.trialExtension;
  return (
    <div className="gp-page">
      <Link to="/customers" className="t-body">
        ‹ Customers
      </Link>
      <PageHead
        title={c.name ?? ""}
        subtitle={
          <span className="gp-row">
            {c.planKey ? (
              <Pill>{PLAN_OPTIONS.find((p) => p.value === c.planKey)?.label ?? c.planKey}</Pill>
            ) : null}
            <StatusPill
              domain="customer"
              value={c.status ?? "pending"}
              label={STATUS_LABEL[c.status ?? "pending"] ?? ""}
            />
            {c.internal ? <Pill>Internal</Pill> : null}
            {sus !== "none" ? (
              <StatusPill
                domain="suspension"
                value={sus}
                label={sus === "blocked" ? "Blocked" : "Suspended"}
              />
            ) : null}
          </span>
        }
        actions={
          <Button variant="primary" onClick={() => setAction("plan")}>
            Change plan
          </Button>
        }
      />
      {sus !== "none" ? (
        <Banner tone="danger">
          This account is{" "}
          {sus === "blocked"
            ? "blocked: the customer app shows only “account blocked”"
            : "suspended: the customer app is read-only"}
          .
        </Banner>
      ) : null}
      <div className="gp-grid-4">
        <StatCard
          icon={<Building20Regular />}
          label="Teams used"
          value={`${c.teamsUsed ?? 0} / ${c.effectiveTeamLimit === -1 ? "∞" : (c.effectiveTeamLimit ?? 0)}`}
          caption={c.usageAsOf ? `As of ${formatDateTime(c.usageAsOf)}` : undefined}
        />
        <StatCard
          icon={<People20Regular />}
          label="External users tracked"
          value={formatNumber(c.externalUsersTracked ?? 0)}
        />
        <StatCard
          icon={<Flag20Regular />}
          label="Campaigns run"
          value={formatNumber(c.campaignsRunCount ?? 0)}
        />
        <StatCard
          icon={<Money20Regular />}
          label="MRR"
          value={formatMoney(c.mrrCents ?? 0)}
          caption={c.internal ? "Internal account: excluded from MRR" : undefined}
        />
      </div>
      <div className="gp-grid-detail">
        <div className="gp-stack">
          <FlagOverrides organizationId={organizationId} detail={c} />
          <Notes organizationId={organizationId} />
          <LifecycleRequests organizationId={organizationId} onNew={() => setAction("request")} />
        </div>
        <div className="gp-stack">
          <Card title="Account">
            <dl className="gp-deflist t-body">
              <dt>Domain</dt>
              <dd>{c.primaryDomain}</dd>
              <dt>Primary contact</dt>
              <dd>
                {c.primaryContact?.name} · {c.primaryContact?.email}
              </dd>
              <dt>Signed up</dt>
              <dd>{formatDate(c.signedUpAt)}</dd>
              <dt>Entra tenant</dt>
              <dd className="t-mono">{c.entraTenantId}</dd>
              <dt>Lifecycle</dt>
              <dd>{c.lifecycleState}</dd>
            </dl>
          </Card>
          <Card title="Billing">
            <dl className="gp-deflist t-body">
              <dt>Price</dt>
              <dd>
                {c.internal
                  ? "No billing (internal)"
                  : `${formatMoney(c.effectivePriceCents)} / ${c.billingInterval === "annual" ? "year" : "month"}`}
              </dd>
              <dt>Next invoice</dt>
              <dd>{formatDate(c.nextInvoiceDate)}</dd>
              <dt>Payment method</dt>
              <dd>{c.paymentMethodLabel ?? "—"}</dd>
              {c.trialEndsAt ? (
                <>
                  <dt>Trial ends</dt>
                  <dd>{formatDate(c.trialEndsAt)}</dd>
                </>
              ) : null}
            </dl>
            {c.latestFailedInvoice ? (
              <Banner tone="danger">
                Last payment failed: {formatMoney(c.latestFailedInvoice.amountCents)} on{" "}
                {formatDate(c.latestFailedInvoice.issuedAt)}.{" "}
                <Link to={`/billing?organizationId=${organizationId}`}>See invoices</Link>
              </Banner>
            ) : null}
            {canExtendTrial ? <Button onClick={() => setAction("trial")}>Extend trial</Button> : null}
            {c.trialExtension ? (
              <p className="t-body-sm gp-muted">
                Trial extended by {c.trialExtension.days} days: {c.trialExtension.reason}
              </p>
            ) : null}
          </Card>
          <Card title="Price and limit overrides">
            {(c.overrides ?? []).length === 0 ? (
              <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
                None. This customer follows the standard tier.
              </p>
            ) : (
              <ul className="gp-stack t-body" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {(c.overrides ?? []).map((o) => (
                  <li key={o.id}>
                    {o.type === "custom_price"
                      ? `Custom price ${formatMoney(o.valueCents)}`
                      : `Teams limit ${o.valueInt === -1 ? "unlimited" : o.valueInt}`}{" "}
                    · {o.reason}
                  </li>
                ))}
              </ul>
            )}
            <Link to="/plans" className="t-body-strong">
              Manage on Plans & pricing
            </Link>
          </Card>
          <Card title="Internal account">
            <Toggle
              label="Internal (demo, test, staff)"
              checked={c.internal ?? false}
              onChange={() => setAction("internal")}
            />
            <p className="t-body-sm gp-muted">
              Internal accounts are left out of MRR, signups, churn, trial callouts and health alerts, and
              have no billing.
            </p>
          </Card>
          <Card title="Danger zone" tone="danger">
            <div className="gp-stack">
              {sus === "none" ? (
                <Button variant="danger" onClick={() => setAction("suspend")}>
                  Suspend or block account
                </Button>
              ) : (
                <Button onClick={() => setAction("lift")}>
                  Lift {sus === "blocked" ? "block" : "suspension"}
                </Button>
              )}
              <Button
                variant="danger"
                disabledReason={
                  c.status === "churned" || c.status === "pending"
                    ? "There is no active subscription to cancel."
                    : undefined
                }
                onClick={() => setAction("cancel")}
              >
                Cancel subscription
              </Button>
            </div>
          </Card>
        </div>
      </div>
      <Dialogs organizationId={organizationId} detail={c} action={action} onClose={() => setAction(null)} />
    </div>
  );
}

function FlagOverrides({ organizationId, detail }: { organizationId: string; detail: Detail }) {
  const flags = useGet<Flag[]>("/feature-flags");
  const fail = useHandleApiError();
  const inv = [`/customers/${organizationId}`, "/feature-flags"];
  const put = useWrite<unknown, Schema<"FlagOverrideWrite">>(
    "PUT",
    "/customers/:id/flag-overrides/:key",
    inv,
  );
  const del = useWrite("DELETE", "/customers/:id/flag-overrides/:key", inv);
  const overrides = new Map((detail.flagOverrides ?? []).map((o) => [o.flagKey, o]));
  const toast = useToast();
  const [set, setSet] = useState<{ key: string; enabled: boolean } | null>(null);
  const [removeKey, setRemoveKey] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  return (
    <Card title="Feature flags">
      {!flags.data ? (
        <Skeleton rows={2} />
      ) : flags.data.length === 0 ? (
        <EmptyState title="No feature flags yet" />
      ) : (
        <ul className="gp-stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {flags.data.map((f) => {
            const o = overrides.get(f.key);
            const enabled = o ? (o.enabled ?? false) : (f.defaultEnabled ?? false);
            return (
              <li key={f.key} className="gp-row" style={{ justifyContent: "space-between" }}>
                <span className="gp-person-text">
                  <span className="t-body-strong t-mono">{f.key}</span>
                  <span className="t-body-sm gp-muted">{f.description}</span>
                </span>
                <span className="gp-row">
                  {o ? <Pill variant="brand">Override</Pill> : null}
                  <Toggle
                    label={`${f.key} for this customer`}
                    checked={enabled}
                    pending={put.isPending}
                    pendingLabel="Saving"
                    onChange={(v) => setSet({ key: f.key ?? "", enabled: v })}
                  />
                  {o ? (
                    <Button variant="ghost" onClick={() => setRemoveKey(f.key ?? "")}>
                      Remove override
                    </Button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={set !== null}
        title={`Turn ${set?.key} ${set?.enabled ? "on" : "off"} for this customer?`}
        description="Overrides the global default for this customer only. The reason is kept in the audit log."
        confirmLabel="Set override"
        busy={put.isPending}
        disabledReason={reason.trim() ? undefined : "A reason is required."}
        onClose={() => {
          setSet(null);
          setReason("");
        }}
        onConfirm={() =>
          set &&
          put.mutate(
            {
              params: { id: organizationId, key: set.key },
              body: { enabled: set.enabled, reason: reason.trim() },
            },
            {
              onSuccess: () => {
                toast.success("Override set");
                setSet(null);
                setReason("");
              },
              onError: fail,
            },
          )
        }
      >
        <Field label="Reason">
          <Input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={removeKey !== null}
        danger
        title={`Remove the ${removeKey} override?`}
        description="The customer follows the global default again."
        confirmLabel="Remove override"
        busy={del.isPending}
        onClose={() => setRemoveKey(null)}
        onConfirm={() =>
          removeKey &&
          del.mutate(
            { params: { id: organizationId, key: removeKey } },
            {
              onSuccess: () => {
                toast.success("Override removed");
                setRemoveKey(null);
              },
              onError: fail,
            },
          )
        }
      />
    </Card>
  );
}

function Notes({ organizationId }: { organizationId: string }) {
  const notes = useGet<Note[]>(`/customers/${organizationId}/notes`);
  const fail = useHandleApiError();
  const toast = useToast();
  const add = useWrite<Note, Schema<"NoteCreate">>("POST", "/customers/:id/notes", [
    `/customers/${organizationId}`,
  ]);
  const [body, setBody] = useState("");
  const [flagged, setFlagged] = useState(false);
  return (
    <Card title="Activity and notes">
      <div className="gp-stack">
        <Field
          label="Add a note"
          help="Notes cannot be edited or deleted. A flagged note turns the account health amber."
        >
          <Textarea value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <Checkbox label="Flag this account" checked={flagged} onChange={setFlagged} />
        <div className="gp-actions">
          <Button
            variant="primary"
            disabledReason={body.trim() ? undefined : "Write the note first."}
            onClick={() =>
              add.mutate(
                { params: { id: organizationId }, body: { body: body.trim(), flagged } },
                {
                  onSuccess: () => {
                    setBody("");
                    setFlagged(false);
                    toast.success("Note added");
                  },
                  onError: fail,
                },
              )
            }
          >
            Add note
          </Button>
        </div>
      </div>
      {!notes.data ? (
        <Skeleton rows={2} />
      ) : notes.data.length === 0 ? (
        <EmptyState title="No activity yet" />
      ) : (
        <Timeline
          events={notes.data.map((n) => ({
            id: n.id ?? "",
            summary: n.body,
            meta: `${n.type === "system" ? "System" : (n.author?.name ?? "Staff")}${n.flagged ? " · flagged" : ""}`,
            at: n.createdAt ?? "",
            atLabel: formatDateTime(n.createdAt),
          }))}
        />
      )}
    </Card>
  );
}

function LifecycleRequests({ organizationId, onNew }: { organizationId: string; onNew: () => void }) {
  const list = useGet<Request[]>(`/customers/${organizationId}/lifecycle-requests`);
  const fail = useHandleApiError();
  const toast = useToast();
  const [target, setTarget] = useState<{ r: Request; kind: "execute" | "cancel" } | null>(null);
  const [person, setPerson] = useState("");
  const [redact, setRedact] = useState("");
  const inv = [`/customers/${organizationId}`];
  const exec = useWrite<Request, { personIdentifier?: string; redactText?: string }>(
    "POST",
    "/lifecycle-requests/:id/execute",
    inv,
  );
  const cancel = useWrite<Request>("POST", "/lifecycle-requests/:id/cancel", inv);
  const extend = useWrite<Request>("POST", "/lifecycle-requests/:id/extend", inv);
  const close = () => {
    setTarget(null);
    setPerson("");
    setRedact("");
  };
  const r = target?.r;
  const needsPerson = r?.type === "anonymise_person" && target?.kind === "execute";
  const needsText = r?.type === "redact" && target?.kind === "execute";
  const waiting = r?.type === "purge" && r.scheduledFor && new Date(r.scheduledFor) > new Date();
  return (
    <Card title="Lifecycle requests" actions={<Button onClick={onNew}>Record a request</Button>}>
      {!list.data ? (
        <Skeleton rows={2} />
      ) : list.data.length === 0 ? (
        <EmptyState title="No requests recorded" />
      ) : (
        <ul className="gp-stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {list.data.map((x) => (
            <li key={x.id} className="gp-row" style={{ justifyContent: "space-between" }}>
              <span className="gp-person-text">
                <span className="t-body-strong">
                  {REQUEST_TYPES.find((t) => t.value === x.type)?.label} · {x.ticketRef}
                </span>
                <span className="t-body-sm gp-muted">
                  {x.requesterName} · {formatDateTime(x.createdAt)}
                  {x.scheduledFor ? ` · runs ${formatDateTime(x.scheduledFor)}` : ""}
                </span>
              </span>
              <span className="gp-row">
                <Pill>{REQUEST_STATUS[x.status ?? "open"]}</Pill>
                {x.status === "open" || x.status === "scheduled" ? (
                  <>
                    {x.type === "switch" ? (
                      <Button
                        variant="ghost"
                        onClick={() =>
                          extend.mutate(
                            { params: { id: x.id ?? "" } },
                            { onSuccess: () => toast.success("Switch extended"), onError: fail },
                          )
                        }
                      >
                        Extend
                      </Button>
                    ) : null}
                    <Button variant="danger" onClick={() => setTarget({ r: x, kind: "execute" })}>
                      Execute
                    </Button>
                    <Button variant="ghost" onClick={() => setTarget({ r: x, kind: "cancel" })}>
                      Cancel
                    </Button>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={target !== null}
        danger
        title={target?.kind === "cancel" ? "Cancel this request?" : "Execute this request?"}
        description={
          target?.kind === "cancel"
            ? "The request is closed without any change to the data."
            : "This is irreversible. It runs once, for this customer, and is audited."
        }
        confirmLabel={target?.kind === "cancel" ? "Cancel request" : "Execute"}
        busy={exec.isPending || cancel.isPending}
        disabledReason={
          target?.kind === "execute" && waiting
            ? "A purge only runs after its 7-day delay."
            : needsPerson && !person.trim()
              ? "Type the guest email or Entra object id."
              : needsText && redact.trim().length < 3
                ? "Type the text to redact (at least 3 characters)."
                : undefined
        }
        onClose={close}
        onConfirm={() => {
          if (!r) return;
          const done = {
            onSuccess: () => {
              toast.success(target?.kind === "cancel" ? "Request cancelled" : "Request executed");
              close();
            },
            onError: fail,
          };
          if (target?.kind === "cancel") cancel.mutate({ params: { id: r.id ?? "" } }, done);
          else
            exec.mutate(
              {
                params: { id: r.id ?? "" },
                body: needsPerson
                  ? { personIdentifier: person.trim() }
                  : needsText
                    ? { redactText: redact.trim() }
                    : {},
              },
              done,
            );
        }}
      >
        {needsText ? (
          <Field
            label="Text to redact, exactly as the customer wrote it"
            help="Typed now and passed on; it is never stored in the console. It is replaced everywhere in the customer's data and in new exports."
          >
            <Input value={redact} onChange={(e) => setRedact(e.target.value)} />
          </Field>
        ) : null}
        {needsPerson ? (
          <Field
            label="Guest email or Entra object id"
            help="Typed now and passed on; it is never stored in the console."
          >
            <Input value={person} onChange={(e) => setPerson(e.target.value)} />
          </Field>
        ) : null}
      </ConfirmDialog>
    </Card>
  );
}

function Dialogs({
  organizationId,
  detail,
  action,
  onClose: closeAction,
}: {
  organizationId: string;
  detail: Detail;
  action: Action;
  onClose: () => void;
}) {
  const fail = useHandleApiError();
  const toast = useToast();
  const inv = [`/customers`, "/dashboard"];
  const plan = useWrite<Detail, Schema<"PlanChange">>("POST", "/customers/:id/plan", inv);
  const cancel = useWrite<Detail, Schema<"CancelSubscription">>("POST", "/customers/:id/cancel", inv);
  const suspend = useWrite<Detail, Schema<"SuspensionSet">>("PUT", "/customers/:id/suspension", inv);
  const lift = useWrite<Detail, Schema<"SuspensionLift">>("DELETE", "/customers/:id/suspension", inv);
  const trial = useWrite<Detail, Schema<"TrialExtend">>("POST", "/customers/:id/trial-extension", inv);
  const internal = useWrite<Detail, Schema<"InternalFlag">>("PUT", "/customers/:id/internal", inv);
  const request = useWrite<Request, Schema<"LifecycleRequestCreate">>(
    "POST",
    "/customers/:id/lifecycle-requests",
    inv,
  );
  const [planKey, setPlanKey] = useState<Schema<"PlanChange">["planKey"]>(
    (detail.planKey as Schema<"PlanChange">["planKey"]) ?? "starter",
  );
  const [interval, setInterval] = useState<Schema<"PlanChange">["billingInterval"]>("monthly");
  const [timing, setTiming] = useState<"period_end" | "immediate">("period_end");
  const [confirmImmediate, setConfirmImmediate] = useState(false);
  const [kind, setKind] = useState<"suspended" | "blocked">("suspended");
  const [category, setCategory] = useState<Schema<"SuspensionSet">["reasonCategory"]>("payment");
  const [text, setText] = useState("");
  const [days, setDays] = useState("14");
  const [rType, setRType] = useState<Schema<"LifecycleRequestCreate">["type"]>("export");
  const [ticket, setTicket] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [verification, setVerification] = useState("");
  const [rStart, setRStart] = useState("");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const id = { id: organizationId };
  const onClose = () => {
    setText("");
    setErrs({});
    setConfirmImmediate(false);
    closeAction();
  };
  const done = (msg: string) => ({
    onSuccess: () => {
      toast.success(msg);
      setText("");
      setErrs({});
      onClose();
    },
    onError: (e: unknown) => {
      setErrs(fieldErrorsOf(e));
      fail(e);
    },
  });
  return (
    <>
      <ConfirmDialog
        open={action === "plan"}
        title="Change plan"
        description="Upgrades apply now with proration; downgrades apply at the next renewal."
        confirmLabel="Change plan"
        busy={plan.isPending}
        onClose={onClose}
        onConfirm={() =>
          plan.mutate(
            { params: id, body: { planKey, billingInterval: interval } },
            done("Plan change sent to billing"),
          )
        }
      >
        <Field label="Plan">
          <SelectX
            value={planKey}
            options={PLAN_OPTIONS}
            onChange={(e) => setPlanKey(e.target.value as typeof planKey)}
          />
        </Field>
        <Field label="Billing">
          <SelectX
            value={interval}
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "annual", label: "Annual" },
            ]}
            onChange={(e) => setInterval(e.target.value as typeof interval)}
          />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === "cancel"}
        danger
        title="Cancel subscription"
        description={
          timing === "immediate"
            ? "The customer app becomes read-only now and data is kept for 90 days."
            : "The subscription ends at the end of the paid period."
        }
        confirmLabel={timing === "immediate" ? "Cancel immediately" : "Cancel at period end"}
        busy={cancel.isPending}
        disabledReason={
          timing === "immediate" && !confirmImmediate
            ? "Confirm the immediate cancellation first."
            : undefined
        }
        onClose={onClose}
        onConfirm={() =>
          cancel.mutate(
            {
              params: id,
              body: {
                timing,
                ...(timing === "immediate" ? { confirmImmediate: true } : {}),
                ...(text ? { reason: text } : {}),
              },
            },
            done("Subscription cancelled"),
          )
        }
      >
        <Field label="When">
          <SelectX
            value={timing}
            options={[
              { value: "period_end", label: "At the end of the paid period" },
              { value: "immediate", label: "Immediately" },
            ]}
            onChange={(e) => {
              setTiming(e.target.value as typeof timing);
              setConfirmImmediate(false);
            }}
          />
        </Field>
        {timing === "immediate" ? (
          <Checkbox
            label="I understand the customer loses write access immediately"
            checked={confirmImmediate}
            onChange={setConfirmImmediate}
          />
        ) : null}
        <Field label="Reason (optional)">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === "suspend"}
        danger
        title="Suspend or block this account"
        description="Customer Admins are emailed. The lifecycle clocks keep running."
        confirmLabel={kind === "blocked" ? "Block account" : "Suspend account"}
        busy={suspend.isPending}
        disabledReason={text.trim() ? undefined : "A reason is required."}
        onClose={onClose}
        onConfirm={() =>
          suspend.mutate(
            { params: id, body: { kind, reasonCategory: category, reasonText: text.trim() } },
            done(kind === "blocked" ? "Account blocked" : "Account suspended"),
          )
        }
      >
        <Field label="Effect">
          <SelectX
            value={kind}
            options={[
              { value: "suspended", label: "Suspended: read-only" },
              { value: "blocked", label: "Blocked: customer app shows only “account blocked”" },
            ]}
            onChange={(e) => setKind(e.target.value as typeof kind)}
          />
        </Field>
        <Field label="Reason category">
          <SelectX
            value={category}
            options={CATEGORIES}
            onChange={(e) => setCategory(e.target.value as typeof category)}
          />
        </Field>
        <Field label="Reason" error={errs.reasonText}>
          <Textarea value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === "lift"}
        title="Lift the suspension"
        description="The customer app returns to its normal state."
        confirmLabel="Lift"
        busy={lift.isPending}
        disabledReason={text.trim() ? undefined : "A reason is required."}
        onClose={onClose}
        onConfirm={() =>
          lift.mutate({ params: id, body: { reasonText: text.trim() } }, done("Suspension lifted"))
        }
      >
        <Field label="Reason">
          <Textarea value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === "trial"}
        title="Extend the trial"
        description="Once per customer, up to 30 days. The trial end date moves and the change is audited."
        confirmLabel="Extend trial"
        busy={trial.isPending}
        disabledReason={
          !text.trim()
            ? "A reason is required."
            : Number(days) < 1 || Number(days) > 30 || !Number.isInteger(Number(days))
              ? "Choose 1 to 30 days."
              : undefined
        }
        onClose={onClose}
        onConfirm={() =>
          trial.mutate(
            { params: id, body: { days: Number(days), reason: text.trim() } },
            done("Trial extended"),
          )
        }
      >
        <Field label="Days (1 to 30)" error={errs.days}>
          <Input type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        <Field label="Reason" error={errs.reason}>
          <Textarea value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === "internal"}
        title={detail.internal ? "Make this a customer account?" : "Mark as internal account?"}
        description={
          detail.internal
            ? "It will count in MRR, signups, churn and health alerts again."
            : "It will be left out of MRR, signups, churn, trial callouts and health alerts."
        }
        confirmLabel="Confirm"
        busy={internal.isPending}
        onClose={onClose}
        onConfirm={() =>
          internal.mutate({ params: id, body: { internal: !detail.internal } }, done("Account updated"))
        }
      />

      <ConfirmDialog
        open={action === "request"}
        danger
        title="Record a lifecycle request"
        description="Verify the requester is a tenant Admin or the Entra tenant owner first. A purge keeps its 7-day delay and Admins are notified."
        confirmLabel="Record request"
        busy={request.isPending}
        disabledReason={
          !ticket.trim() || !rName.trim() || !rEmail.trim() || !verification.trim()
            ? "Fill in every field."
            : undefined
        }
        onClose={onClose}
        onConfirm={() =>
          request.mutate(
            {
              params: id,
              body: {
                type: rType,
                ticketRef: ticket.trim(),
                requesterName: rName.trim(),
                requesterEmail: rEmail.trim(),
                verificationNotes: verification.trim(),
                ...(rType === "switch" && rStart ? { transitionStartsOn: rStart } : {}),
              },
            },
            done("Request recorded"),
          )
        }
      >
        <Field label="Type">
          <SelectX
            value={rType}
            options={REQUEST_TYPES}
            onChange={(e) => setRType(e.target.value as typeof rType)}
          />
        </Field>
        {rType === "switch" ? (
          <Field label="Transitional period starts on (30 days; today if empty)">
            <Input type="date" value={rStart} onChange={(e) => setRStart(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Ticket reference" error={errs.ticketRef}>
          <Input value={ticket} onChange={(e) => setTicket(e.target.value)} />
        </Field>
        <Field label="Requester name">
          <Input value={rName} onChange={(e) => setRName(e.target.value)} />
        </Field>
        <Field label="Requester email" error={errs.requesterEmail}>
          <Input type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} />
        </Field>
        <Field label="How the requester was verified">
          <Textarea value={verification} maxLength={2000} onChange={(e) => setVerification(e.target.value)} />
        </Field>
      </ConfirmDialog>
    </>
  );
}
